import axios from 'axios';
import { URL_FOUNDATION } from '../../config/keys';
import { CreationEditionOrDeletion } from '../settings/subSettings/statusesAndTagsSettings';

export type DashboardType = 'dashboard' | 'company' | 'product';
export const CHART_REPRESENTATIONS = ['bar', 'line', 'pie', 'number', 'gauge', 'text', 'image'] as const;
export type ChartRepresentation = typeof CHART_REPRESENTATIONS[number];
export function isStaticChart(representation: ChartRepresentation): boolean {
	return representation === 'text' || representation === 'image';
}
export function isSingleValueChart(representation: string): boolean {
	return representation === 'number' || representation === 'gauge';
}
export const TEXT_SIZES = ['small', 'medium', 'large'] as const;
type TextSize = typeof TEXT_SIZES[number];
export const TEXT_ALIGNMENTS = ['left', 'center', 'right'] as const;
type TextAlignment = typeof TEXT_ALIGNMENTS[number];
export const IMAGE_FITS = ['contain', 'cover'] as const;
type ImageFit = typeof IMAGE_FITS[number];
export const CHART_GRANULARITIES = ['day', 'week', 'month', 'year'] as const;
type ChartGranularity = typeof CHART_GRANULARITIES[number];
export const CHART_ORIENTATIONS = ['horizontal', 'vertical'] as const;
type ChartOrientation = typeof CHART_ORIENTATIONS[number];
export const CHART_SORT_ORDERS = ['asc', 'desc', 'none'] as const;
type ChartSortOrder = typeof CHART_SORT_ORDERS[number];
export const DASHBOARD_TYPES = ['dashboard', 'company'] as const;

type FilterPathItem = {
  path: string[];
  updatable?: boolean;
};

type FiltersPath = Record<number, FilterPathItem>;

type ChartOptions = {
  granularity?: ChartGranularity;
  number_extension?: string;
  orientation?: ChartOrientation;
  legend?: boolean;
  legend_item_spacing?: number;
  sort?: ChartSortOrder;
  max_values?: number;
  digits_after_decimal?: number;
  color_gradient?: boolean;
  row_span?: number;
  column_span?: number;
  background_color?: string;
  legend_orientation?: ChartOrientation;
  max_legend_items?: number;
  stacked?: boolean;
  margins?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  trim_x_labels?: number;
  trim_y_labels?: number;
  x_axis_legend?: string;
  y_axis_legend?: string;
  x_axis_boundaries?: {min?: number, max?: number};
  y_axis_boundaries?: {min?: number, max?: number};
  distance_from_center?: number; // pie charts
  min_slice_angle?: number; // pie charts
  hide_value_labels?: boolean; // pie charts
  doughnut?: boolean; // pie charts
  display_line_chart_evolution?: boolean; // number charts
  is_percentage?: boolean; // number charts
  gauge_boundaries?: {min?: number, max?: number};
  text?: string;
  text_size?: TextSize;
  text_align?: TextAlignment;
  image?: string;
  image_fit?: ImageFit;
};

type AiOptions = {
  instructions?: string;
};

export type ChartData = {
  panel_index: number;
  values_index?: number;
  group_by_index: number;
  footer_values_only?: boolean;
  filters_path?: FiltersPath;
  options?: ChartOptions;
  ai_options?: AiOptions;
  goals?: boolean;
};

export type DashboardChartConfigWithChartData = DashboardChartConfig & {
  data: ChartData;
  representation: ChartRepresentation;
  report_id: number | null;
  name?: string;
};

type PostDashboardChart = {
  data: ChartData;
  representation: ChartRepresentation;
  report_id: number | null;
  name?: string;
  linked_dashboards?: string[];
};

export type DashboardChartConfig = {
  uuid: string;
  sequence: number;
  // position [x, y, z] ??
};

export type Dashboard = {
  uuid: string;
  name: string;
  type: DashboardType;
  charts: DashboardChartConfigWithChartData[];
  sequence: number;
};

type PostDashboard = {
  name: string;
  type: DashboardType;
  charts: DashboardChartConfig[];
  sequence: number;
};

export type PutDashboard = {
  id?: string;
  name?: string;
  type?: DashboardType;
  charts?: DashboardChartConfig[];
  sequence?: number;
  deleted?: boolean;
};

export function getDashboards(type?: DashboardType): Promise<Dashboard[]> {
	return axios
		.get<Dashboard[]>(
			`${URL_FOUNDATION}/api/v2/dashboard${type ? `?type=${type}` : ''}`
		)
		.then(res => res.data);
}

export function postDashboard(dashboard: PostDashboard): Promise<Dashboard> {
	return axios
		.post<Dashboard>(`${URL_FOUNDATION}/api/v2/dashboard`, dashboard)
		.then(res => res.data);
}

export function putDashboard(
	dashboardUuid: string,
	dashboard: PostDashboard
): Promise<Dashboard> {
	return axios
		.put<Dashboard>(
			`${URL_FOUNDATION}/api/v2/dashboard/${dashboardUuid}`,
			dashboard
		)
		.then(res => res.data);
}

export function bulkPutDashboards(
	dashboards: CreationEditionOrDeletion<PutDashboard>[]
): Promise<Dashboard[]> {
	return axios
		.put<Dashboard[]>(`${URL_FOUNDATION}/api/v2/dashboard`, dashboards)
		.then(res => res.data);
}

export function deleteDashboard(dashboardUuid: string): Promise<void> {
	return axios
		.delete<void>(`${URL_FOUNDATION}/api/v2/dashboard/${dashboardUuid}`)
		.then(res => res.data);
}

export function getCharts(): Promise<DashboardChartConfigWithChartData[]> {
	return axios
		.get<DashboardChartConfigWithChartData[]>(
			`${URL_FOUNDATION}/api/v2/dashboard/chart`
		)
		.then(res => res.data);
}

export function postChart(
	chart: PostDashboardChart
): Promise<DashboardChartConfigWithChartData> {
	return axios
		.post<DashboardChartConfigWithChartData>(
			`${URL_FOUNDATION}/api/v2/dashboard/chart`,
			chart
		)
		.then(res => res.data);
}

export function putChart(
	chartUuid: string,
	chart: PostDashboardChart
): Promise<DashboardChartConfigWithChartData> {
	return axios
		.put<DashboardChartConfigWithChartData>(
			`${URL_FOUNDATION}/api/v2/dashboard/chart/${chartUuid}`,
			chart
		)
		.then(res => res.data);
}

export function deleteChart(chartUuid: string): Promise<void> {
	return axios
		.delete<void>(`${URL_FOUNDATION}/api/v2/dashboard/chart/${chartUuid}`)
		.then(res => res.data);
}
